/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { Link } from "react-router-dom";
import {
  Col,
  FlexContainer,
  Spinner,
  SVGIcon,
  Text,
  theme,
  Tooltip,
} from "@nordcloud/gnui";
import { isNotEmpty } from "~/tools";
import { LinkButton } from "../Styled";

type Props = {
  label?: string;
  value: string;
  description?: string;
  url?: string;
  size?: "lg" | "md" | "xl";
  weight?: "bold" | "medium" | "regular";
  padding?: string;
  grow?: boolean;
  align?: "left" | "right";
  valueColor?: string;
  loading?: boolean;
  tooltipText?: string;
  tooltipPosition?: "left" | "right";
  linkText?: string;
  onClick?: () => void;
  mb?: string;
  itemWidth?: string;
  labelLink?: string;
  valueFontWeight?: number;
};

export function DataItem({
  label,
  value,
  url,
  size = "xl",
  weight = "medium",
  description,
  padding = `0 ${theme.spacing.spacing03}`,
  grow = true,
  align = "left",
  valueColor = theme.color.text.text01,
  loading = false,
  tooltipText,
  tooltipPosition = undefined,
  linkText,
  onClick,
  mb,
  itemWidth,
  labelLink,
  valueFontWeight,
}: Props) {
  const width = itemWidth || "10.25rem";
  return (
    <Col
      css={{
        flexGrow: grow ? 1 : 0,
        minWidth: grow ? "unset" : width,
        padding,
        textAlign: align === "left" ? "start" : "end",
        width,
      }}
    >
      <When condition={label}>
        <FlexContainer>
          <Text
            isTitle
            size="sm"
            tag="span"
            color={theme.color.text.text02}
            mb={mb}
            data-testid={
              label ? label.replaceAll(/\s/g, "-").toLowerCase() : ""
            }
          >
            <If condition={labelLink}>
              <Then>
                <Link
                  to={labelLink ?? ""}
                  css={{
                    "&:hover": {
                      textDecorationColor: theme.color.text.text04,
                    },
                  }}
                >
                  {label}
                </Link>
              </Then>
              <Else>{label}</Else>
            </If>
          </Text>
          <When condition={tooltipText}>
            <Tooltip caption={tooltipText} position={tooltipPosition}>
              <SVGIcon
                name="info"
                size="sm"
                color="black"
                css={{
                  marginLeft: theme.spacing.spacing01,
                  marginRight: theme.spacing.spacing01,
                }}
              />
            </Tooltip>
          </When>
        </FlexContainer>
      </When>
      <If condition={loading}>
        <Then>
          <FlexContainer justifyContent={align === "left" ? "start" : "end"}>
            <Spinner />
          </FlexContainer>
        </Then>
        <Else>
          <Text
            size={size}
            weight={weight}
            color={valueColor}
            m={0}
            as="section"
            css={{ overflowWrap: "break-word", fontWeight: valueFontWeight }}
            data-testid={
              label ? label.replaceAll(/\s/g, "-").toLowerCase() + "-value" : ""
            }
          >
            <If condition={url}>
              <Then>
                <Link to={url ?? ""}>{value}</Link>
              </Then>
              <Else>{value}</Else>
            </If>

            <When condition={description}>
              <Text size="sm" color={theme.color.text.text02}>
                {description}
              </Text>
            </When>
            <When condition={isNotEmpty(linkText ?? "")}>
              <LinkButton
                css={{
                  fontSize: theme.fontSizes.md,
                  marginLeft: theme.spacing.spacing02,
                }}
                onClick={onClick}
              >
                {linkText}
              </LinkButton>
            </When>
          </Text>
        </Else>
      </If>
    </Col>
  );
}
